import { ScreenSize } from 'glints-aries';
import { Blue, Neutral } from 'glints-aries/lib/@next/utilities/colors';
import styled, { keyframes } from 'styled-components';

import GlintsPicture from 'src/components/GlintsPicture';

import { CONTENT_MAX_WIDTH, MOBILE_BREAKPOINT } from '../constants';

export const Section = styled.section`
  position: relative;
  background: ${Neutral.B100};
  padding: 48px 0;
  border-bottom: 1px solid ${Neutral.B85};

  @media (max-width: ${ScreenSize.tablet}px) {
    padding: 36px 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 28px 0;
  }
`;

export const Inner = styled.div`
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 0 24px;
`;

export const Eyebrow = styled.h2`
  text-align: center;
  color: ${Neutral.B40};
  font-size: 12.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  margin: 0 0 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 11.5px;
    margin-bottom: 18px;
  }
`;

export const MarqueeViewport = styled.div`
  position: relative;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent,
    #000 8%,
    #000 92%,
    transparent
  );
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 8%,
    #000 92%,
    transparent
  );

  &:focus-visible {
    outline: 2px solid ${Blue.Brand};
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    overflow-x: auto;
    -webkit-mask-image: none;
    mask-image: none;
  }
`;

const scroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

export const MarqueeTrack = styled.div<{ $paused: boolean }>`
  display: flex;
  width: max-content;
  animation: ${scroll} 50s linear infinite;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};

  /* Hover-pause is a convenience; the explicit PauseButton is the
     keyboard/AT-accessible control required by WCAG 2.2.2. */
  ${MarqueeViewport}:hover & {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    & > [aria-hidden='true'] {
      display: none;
    }
  }

  @media (max-width: ${ScreenSize.tablet}px) {
    animation-duration: 35s;
  }
`;

export const MarqueeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  padding-right: 32px;
  flex-shrink: 0;

  @media (max-width: ${ScreenSize.tablet}px) {
    gap: 24px;
    padding-right: 24px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 20px;
    padding-right: 20px;
  }
`;

interface LogoItemProps {
  $scale: number;
  $tintBlack: boolean;
}

const BASE_LOGO_HEIGHT = 44;
const MOBILE_LOGO_HEIGHT = 32;

export const LogoItem = styled.div<LogoItemProps>`
  flex-shrink: 0;
  height: ${({ $scale }) => Math.round(BASE_LOGO_HEIGHT * $scale)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $tintBlack }) => ($tintBlack ? 0.85 : 0.65)};
  filter: ${({ $tintBlack }) =>
    $tintBlack ? 'brightness(0)' : 'grayscale(0.2)'};
  transition:
    opacity 0.2s ease,
    filter 0.2s ease;

  &:hover {
    opacity: 1;
    filter: ${({ $tintBlack }) =>
      $tintBlack ? 'brightness(0)' : 'grayscale(0)'};
  }

  & > * {
    height: 100%;
    display: block;
  }

  & img {
    height: 100%;
    width: auto;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${({ $scale }) => Math.round(MOBILE_LOGO_HEIGHT * $scale)}px;
  }
`;

export const LogoImage = styled(GlintsPicture)``;

// Keyboard/AT-accessible pause toggle for the auto-scrolling marquee (WCAG
// 2.2.2). Hidden under reduced-motion since the track isn't animating then,
// so there is nothing to pause.
export const PauseButton = styled.button`
  position: absolute;
  top: 40px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: thin solid ${Neutral.B85};
  border-radius: 50%;
  background: ${Neutral.B100};
  color: ${Neutral.B40};
  cursor: pointer;
  transition:
    color 0.2s ease,
    border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
  }

  &:hover {
    color: ${Neutral.B18};
    border-color: ${Neutral.B40};
  }

  &:focus-visible {
    outline: 2px solid ${Blue.Brand};
    outline-offset: 2px;
  }

  @media (max-width: ${ScreenSize.tablet}px) {
    top: 28px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    top: 20px;
    right: 16px;
    width: 28px;
    height: 28px;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;
